import { useState } from "react";
import { I } from "../Icons.jsx";
import IconBtn from "../ui/IconBtn.jsx";

export default function GroupCard({
  group, gi,
  defaultSize,
  people,
  showHosts,
  selectedP,
  onDragOver, onDrop,
  onPersonClick, onPersonDragStart,
  onToggleHost, onMoveUp, onMoveDown, onRemove, onSwap,
  onDeleteGroup,
}) {
  const effCap = group.capacity ?? defaultSize ?? null;
  const over = effCap != null && group.people.length > effCap;
  const under = effCap != null && group.people.length < effCap;
  const host = group.host ? people.find(p => p.id === group.host) : null;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onDragOver={(e) => { onDragOver(e); setHovered(true); }}
      onDragLeave={() => setHovered(false)}
      onDrop={(e) => { setHovered(false); onDrop(e); }}
      style={{
        borderRadius: 12,
        border: `1px solid ${hovered ? "var(--ink)" : "var(--line-2)"}`,
        background: "var(--paper-0)",
        overflow: "hidden",
        boxShadow: hovered ? "0 0 0 3px var(--paper-2)" : "0 1px 2px rgba(34,28,20,0.03)",
        transition: "border-color 0.12s, box-shadow 0.12s",
      }}
    >
      {/* Header */}
      <div style={{
        padding: "12px 14px 10px",
        borderBottom: "1px solid var(--line)",
        display: "flex", alignItems: "flex-start", gap: 10,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{
              fontFamily: "var(--display)",
              fontSize: 11, color: "var(--ink-3)",
              fontStyle: "italic",
              fontVariantNumeric: "tabular-nums",
              minWidth: 18,
            }}>№{String(gi + 1).padStart(2, "0")}</span>
            <span style={{
              fontSize: 14, fontWeight: 600, color: "var(--ink)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              letterSpacing: -0.05,
              flex: 1,
            }}>{group.name}</span>
          </div>
          {host && showHosts && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              marginTop: 4, fontSize: 11, color: "var(--accent)", fontWeight: 500,
            }}>
              <I.star size={10} stroke="currentColor" /> Hosted by {host.Name.split(" ")[0]}
            </div>
          )}
        </div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "3px 8px", borderRadius: 999,
          background: over ? "var(--warn-soft)" : under ? "var(--paper-1)" : "var(--accent-soft)",
          color: over ? "var(--warn)" : under ? "var(--ink-3)" : "var(--accent)",
          border: `1px solid ${over ? "var(--warn-line)" : under ? "var(--line)" : "var(--accent)"}`,
          fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums",
          flexShrink: 0,
        }}>
          {group.people.length}{effCap != null && <>/<span style={{ opacity: 0.6 }}>{effCap}</span></>}
        </div>
        <IconBtn icon={I.x} title="Delete group" size={12} onClick={onDeleteGroup} />
      </div>

      {over && effCap != null && (
        <div style={{
          padding: "5px 14px",
          fontSize: 11,
          color: "var(--warn-ink)",
          background: "var(--warn-soft)",
          borderBottom: "1px solid var(--warn-line)",
        }}>
          {group.people.length - effCap} over the suggested max of {effCap}
        </div>
      )}

      {/* Body */}
      <div style={{
        padding: "8px 10px 10px", minHeight: 56,
        display: "flex", flexDirection: "column", gap: 1,
      }}>
        {group.people.length === 0 && (
          <div style={{
            padding: "12px 6px",
            fontSize: 12, color: "var(--ink-4)", fontStyle: "italic",
            textAlign: "center",
          }}>
            Drag guests here
          </div>
        )}
        {group.people.map((pid, idx) => {
          const p = people.find(x => x.id === pid);
          if (!p) return null;
          const isHost = group.host === pid;
          return (
            <div
              key={pid}
              className="group-row"
              draggable
              onDragStart={(e) => onPersonDragStart(e, pid)}
              onClick={() => onPersonClick(pid)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "5px 8px", borderRadius: 6,
                background: selectedP === pid ? "var(--paper-2)" : "transparent",
                cursor: "grab",
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) => { if (selectedP !== pid) e.currentTarget.style.background = "var(--paper-1)"; }}
              onMouseLeave={(e) => { if (selectedP !== pid) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{
                fontFamily: "var(--display)", fontStyle: "italic",
                fontSize: 11, color: "var(--ink-4)",
                width: 16, textAlign: "right", fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
              }}>{idx + 1}</span>
              <div style={{
                width: 22, height: 22, borderRadius: "50%",
                background: isHost && showHosts ? "var(--accent)" : "var(--paper-2)",
                color: isHost && showHosts ? "var(--paper-0)" : "var(--ink-2)",
                border: `1px solid ${isHost && showHosts ? "var(--accent)" : "var(--line)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9.5, fontWeight: 600, flexShrink: 0, letterSpacing: 0.3,
              }}>
                {isHost && showHosts
                  ? <I.star size={10} stroke="currentColor" />
                  : p.Name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, color: "var(--ink)",
                  fontWeight: isHost && showHosts ? 600 : 450,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{p.Name}</div>
                {p.Dietary && <div style={{ fontSize: 10.5, color: "var(--ink-3)", marginTop: 0 }}>{p.Dietary}</div>}
              </div>
              <div className="row-actions" style={{ display: "flex", gap: 1 }}>
                <IconBtn icon={I.star} title={isHost ? "Remove host" : "Set host"} size={12} tone={isHost && showHosts ? "star" : "ghost"} onClick={(e) => { e.stopPropagation(); onToggleHost(pid); }} />
                <IconBtn icon={I.chevronUp} title="Move up" size={12} disabled={idx === 0} onClick={(e) => { e.stopPropagation(); onMoveUp(pid); }} />
                <IconBtn icon={I.chevronDown} title="Move down" size={12} disabled={idx === group.people.length - 1} onClick={(e) => { e.stopPropagation(); onMoveDown(pid); }} />
                <IconBtn icon={I.swap} title="Swap" size={12} onClick={(e) => { e.stopPropagation(); onSwap(pid); }} />
                <IconBtn icon={I.x} title="Remove" size={12} onClick={(e) => { e.stopPropagation(); onRemove(pid); }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
